import {
  getBulkModifiersDetails,
  handlePointModifers,
  postBulkPointModifiersDraft,
  putBulkPointModifiersDraft,
  validateBulkPointModifierName
} from '@/services/bulkModifiers';
import {useAppDispatch, useAppSelector} from '@/store';
import {
  resetBulkModifiers,
  setBulkModifiers
} from '@/store/slices/bulkPointSlice';
import {setToastNotifications} from '@/store/slices/commonSlice';
import {
  API_STATUS,
  SCHEDULE_DROPDOWN,
  STATUS_VALUES
} from '@/utils/constants/commonConstants';
import {ROUTES} from '@/utils/constants/routesConstants';
import {useCallback, useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {
  getBulkPointModifierStartDateUtc,
  getFormattedDateString,
  getLocalDateTimeDDMMYYYY,
  getUUID,
  handleNameValidationViaCallbackFn
} from '@/utils/commons';
import {validate} from 'uuid';
import {useErrorHandler} from '@/hooks/useErrorHandler';
import {useActionDropDown} from '@/hooks/useActionDropDown';

export default function useReviewBulkPointModifier() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const {handleApiError} = useErrorHandler();
  const bulkModifier = useAppSelector((state) => state?.bulkModifiers);
  const {dropdownList: dropdownListActions} = useActionDropDown();
  const {id} = useParams();
  const [loading, setLoading] = useState(false);
  const handleCancel = () => {
    dispatch(resetBulkModifiers());
    navigate(ROUTES.BULK_POINT_MODIFIERS);
  };

  const getActionsValue = (actions) => {
    if (actions && dropdownListActions) {
      return dropdownListActions?.find((item) => item.displayText === actions)
        ?.value;
    }
    return null;
  };

  const getBulkModifierParams = () => {
    const existingUUID = bulkModifier?.uuid;
    const modifierName = bulkModifier?.bulkModifierName || bulkModifier?.name;

    const scheduleVal = bulkModifier?.schedule?.toLowerCase();
    const shouldUseScheduleComputedStartDate = [
      SCHEDULE_DROPDOWN.NOW,
      SCHEDULE_DROPDOWN.END_OF_MONTH,
      SCHEDULE_DROPDOWN.END_OF_YEAR,
      SCHEDULE_DROPDOWN.CUSTOM_SCHEDULE
    ].includes(scheduleVal);

    return {
      storeSelectorName: bulkModifier?.storeSelector,
      name: bulkModifier?.bulkModifierName || bulkModifier?.name,
      noOfDays: bulkModifier?.numberOfDays,
      noOfPoints: bulkModifier?.numberOfPoints,
      schedule: bulkModifier?.schedule,
      startDate: shouldUseScheduleComputedStartDate
        ? getBulkPointModifierStartDateUtc({
            schedule: bulkModifier?.schedule,
            startDate: bulkModifier?.startDate
          })
        : bulkModifier?.startDate &&
          getFormattedDateString(bulkModifier.startDate),
      file: bulkModifier?.storeList || bulkModifier?.file,
      action: getActionsValue(bulkModifier?.actions),
      uuid: existingUUID || getUUID(modifierName)
    };
  };

  const handlePublish = async () => {
    const param = getBulkModifierParams();
    try {
      let response = await handlePointModifers({data: param});
      if (response?.success) {
        dispatch(setBulkModifiers(bulkModifier));
        dispatch(
          setToastNotifications({
            show: true,
            message: 'New Bulk point modifier published',
            type: 'success'
          })
        );
        dispatch(resetBulkModifiers());
        navigate(ROUTES.BULK_POINT_MODIFIERS);
      } else {
        const errMsg = response?.errorMessage || 'An unknown error occurred';
        dispatch(
          setToastNotifications({show: true, message: errMsg, type: 'error'})
        );
      }
    } catch (error) {
      const errMsg =
        error?.response?.data?.errorMessage ||
        error?.message ||
        'An unknown error occurred';
      dispatch(
        setToastNotifications({show: true, message: errMsg, type: 'error'})
      );
    }
  };

  const handleDraft = async () => {
    const existingUUID = bulkModifier?.uuid;
    const param = {
      ...getBulkModifierParams(),
      status: API_STATUS.DRAFT
    };
    const callPutApi = !!existingUUID;
    let response = callPutApi
      ? await putBulkPointModifiersDraft({data: param})
      : await postBulkPointModifiersDraft({data: param});
    if (response?.success) {
      dispatch(
        setToastNotifications({
          show: true,
          message: callPutApi
            ? 'Bulk point modifier updated'
            : 'New Bulk point modifier saved',
          type: 'success'
        })
      );
      dispatch(resetBulkModifiers());
      navigate(ROUTES.BULK_POINT_MODIFIERS);
    } else {
      dispatch(
        setToastNotifications({
          show: true,
          message: 'Failed to save draft',
          type: 'error'
        })
      );
    }
  };

  const handleNameValidation = async (queryParams) => {
    const data = await handleNameValidationViaCallbackFn(
      validateBulkPointModifierName,
      queryParams
    );
    return data;
  };

  const handleGetBulkModifierDetails = useCallback(async () => {
    if (id && validate(id)) {
      return await getBulkModifiersDetails(id, 0, 10, 'uuid');
    } else {
      return await getBulkModifiersDetails(id, 0, 10, 'id');
    }
  }, [id]);

  const getBulkModifierDetailsById = useCallback(async () => {
    try {
      setLoading(true);
      const res = await handleGetBulkModifierDetails();
      setLoading(false);
      const result = res?.data[0];
      const updatedResponse = {
        actions: result?.action,
        schedule: result?.schedule,
        storeSelector: result?.storeSelectorName,
        bulkModifierName: result?.bulkModifierName || result?.name,
        file: result?.file,
        name: result?.name,
        numberOfPoints: result?.noOfPoints,
        numberOfDays: result?.noOfDays,
        uuid: result?.uuid || '',
        status: result?.status || STATUS_VALUES.Published,
        scheduledAt: getLocalDateTimeDDMMYYYY(result?.scheduledAt, {
          separator: '-'
        }),
        startDate: getLocalDateTimeDDMMYYYY(result?.startDate, {separator: '-'})
      };
      dispatch(setBulkModifiers(updatedResponse));
    } catch (error) {
      setLoading(false);
      handleApiError(error);
    }
  }, [handleGetBulkModifierDetails, dispatch, handleApiError]);

  useEffect(() => {
    if (id) {
      getBulkModifierDetailsById();
    }
  }, [getBulkModifierDetailsById, id]);

  return {
    handleCancel,
    handlePublish,
    handleDraft,
    handleNameValidation,
    loading
  };
}
